import type { ReactNode } from "react";
import React, { createContext, useCallback, useState } from "react";

import type { CartData } from "../../cart-widget/mockData";
import type { MediaItem, PlaylistContextProps } from "../types/contexts";
import {
  transformLibraryToMediaItemsWithCart,
  transformMediaToMediaItemWithCart,
} from "../utils/dataTransformers";
import { getFallbackMediaItem } from "../utils/mediaUtils";

// Export the context
export const PlaylistContext = createContext<PlaylistContextProps | undefined>(
  undefined,
);

export const PlaylistProvider: React.FC<{
  children: ReactNode;
  initialMediaItems: MediaItem[];
  initialCartData?: CartData | null;
}> = ({ children, initialMediaItems, initialCartData }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [cartItems, setCartItems] = useState<CartData>(
    initialCartData ?? {
      id: 0,
      items: [],
      sub_total_in_currency: "$0.00",
      recurring: [],
    },
  );
  const [mediaItems, setMediaItems] = useState<MediaItem[]>(initialMediaItems);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // New state for data fetching
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store the last fetched IDs for refresh functionality
  const [lastLibraryId, setLastLibraryId] = useState<string | null>(null);
  const [lastMediaId, setLastMediaId] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchLibraryData = useCallback(async (libraryId: string) => {
    setIsLoading(true);
    setError(null);
    setLastLibraryId(libraryId);
    setLastMediaId(null);

    try {
      if (typeof window !== "undefined") {
        const libraryResponse =
          await window.FluidCommerceSDK.getLibrary(libraryId);
        const { mediaItems: transformedItems, cartData } =
          transformLibraryToMediaItemsWithCart(libraryResponse);
        setMediaItems(transformedItems);
        setCurrentMediaIndex(0); // Reset to first item
        setCartItems(cartData);
      } else {
        throw new Error("FluidCommerceSDK not available");
      }
    } catch (err) {
      console.error("Error fetching library data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch library data",
      );
      // Fallback to a single fallback item
      setMediaItems([getFallbackMediaItem(1)]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMediaData = useCallback(async (mediaId: string) => {
    setIsLoading(true);
    setError(null);
    setLastMediaId(mediaId);
    setLastLibraryId(null);

    try {
      if (typeof window !== "undefined") {
        const mediaResponse = await window.FluidCommerceSDK.getMedia(mediaId);
        const { mediaItem, cartData } =
          transformMediaToMediaItemWithCart(mediaResponse);
        setMediaItems([mediaItem]);
        setCurrentMediaIndex(0); // Reset to first item
        setCartItems(cartData);
      } else {
        throw new Error("FluidCommerceSDK not available");
      }
    } catch (err) {
      console.error("Error fetching media data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch media data",
      );
      // Fallback to a single fallback item
      setMediaItems([getFallbackMediaItem(1)]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshData = useCallback(() => {
    if (lastLibraryId) {
      void fetchLibraryData(lastLibraryId);
    } else if (lastMediaId) {
      void fetchMediaData(lastMediaId);
    }
  }, [lastLibraryId, lastMediaId, fetchLibraryData, fetchMediaData]);

  return (
    <PlaylistContext.Provider
      value={{
        currentMediaIndex,
        setCurrentMediaIndex,
        isMuted,
        setIsMuted,
        isPlaying,
        setIsPlaying,
        mediaItems,
        setMediaItems,
        isDescriptionExpanded,
        setIsDescriptionExpanded,
        isShareOpen,
        setIsShareOpen,
        isCartOpen,
        setIsCartOpen,
        cartItems,
        setCartItems,
        // New properties
        isLoading,
        error,
        fetchLibraryData,
        fetchMediaData,
        refreshData,
        clearError,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
};
